// ─────────────────────────────────────────────
// src/components/ChatBot.jsx
// ─────────────────────────────────────────────
import { useState, useRef, useEffect } from "react";

const BOT_NAME = "Nova";
const BOT_AVATAR = "🤖";

// ── Knowledge base ──────────────────────────────────────────────────────────
const KB = [
  {
    tags: ["age", "old", "grade", "kindergarten", "k-5", "elementary", "young", "how old"],
    answer: "Mars Academy is designed for elementary and middle school students, starting from kindergarten. Our program is built to grow with each child, so it works no matter what grade they're in. 🚀",
  },
  {
    tags: ["experience", "prior", "beginner", "never", "know nothing", "background", "prerequisite"],
    answer: "No prior experience needed at all! Our program starts from the very beginning. Whether your child has never touched a keyboard or is already curious about coding, Mars Academy meets them exactly where they are. 😊",
  },
  {
    tags: ["how long", "duration", "length", "weeks", "months", "session length", "how many classes"],
    answer: "Each session typically runs 6 classes, 1 per week, with each class about 2 hours long — plenty of time to dig in, explore, and actually build real things! Exact schedule depends on your school.",
  },
  {
    tags: ["virtual", "online", "in-person", "remote", "school", "location", "where", "area", "serve", "fairfax", "virginia"],
    answer: "Mars Academy classes are held in-person, right at your child's school. We currently serve schools in Fairfax, Virginia, and we're always looking to expand into new communities — reach out at info@marsacademy.ai if your school isn't on the list yet!",
  },
  {
    tags: ["laptop", "computer", "equipment", "device", "bring", "need", "scratch"],
    answer: "Laptops are provided to all students during class — no need to bring your own! Your child will need a free Scratch account at scratch.mit.edu before the first session. We'll send setup instructions after enrollment. 💻",
  },
  {
    tags: ["price", "cost", "how much", "fee", "pay", "payment", "charge", "dollar", "pricing"],
    answer: "For pricing information, please reach out to us directly and we'll be happy to help!\n📧 info@marsacademy.ai\n📞 (202)-596-5089",
  },
  {
    tags: ["refund", "cancel", "cancellation", "money back", "withdraw"],
    answer: "If your school's PTA handles registration, refunds are subject to your PTA's policies. If no PTA is handling registration, please email us at info@marsacademy.ai — refund situations are reviewed individually. Please note that missing a class is not considered a valid reason for a refund.",
  },
  {
    tags: ["learn", "curriculum", "subject", "topic", "what do", "study", "class cover", "content"],
    answer: "In one program, kids cover:\n🖥️ Computer Basics & Navigation\n🔐 Internet Safety & Cybersecurity\n🐱 Programming with Scratch\n🤖 Artificial Intelligence\n☁️ The Cloud\n🏢 Digital Infrastructure\n\nEvery concept is hands-on — kids code, build, and create. No passive watching!",
  },
  {
    tags: ["safe", "safety", "security", "online safety", "cybersecurity", "protect", "privacy"],
    answer: "Internet safety and cybersecurity is a core part of our curriculum. Kids learn smart online habits including password safety, privacy practices, and how to stay safe in a connected world. We take child safety very seriously both in our content and in how we handle your data. 🔐",
  },
  {
    tags: ["ai", "artificial intelligence", "machine learning", "robot"],
    answer: "We teach age-appropriate AI literacy — kids discover how machines learn, how AI shapes the world around them, and develop a healthy, informed perspective on the technology they'll grow up with. 🤖",
  },
  {
    tags: ["idca", "certified", "certification", "international data center", "authority", "accredited"],
    answer: "Yes! Mars Academy's curriculum is certified by the International Data Center Authority (IDCA) — the world's leading global digital economy think tank. IDCA certification means our content has been independently reviewed against rigorous industry standards used by real-world data centers, cloud providers, and tech enterprises. It's a genuine mark of quality. ✅",
  },
  {
    tags: ["enroll", "sign up", "register", "join", "how to", "start", "get started"],
    answer: "Registration depends on your school — at some schools it's handled through the PTA, while at others you can sign up directly on our website. If your school uses the website, here's how:\n\n1️⃣ **Create an account** — Click the 'Sign Up' button at the top of the page and enter your name, email, and a password.\n\n2️⃣ **Add your child** — Once logged in, you'll land on your parent dashboard. Click 'Add a Student' and fill in your child's name, grade, and a few quick details.\n\n3️⃣ **Pick a school & session** — Under the Enroll tab, choose the school location nearest you and select an available session.\n\n4️⃣ **Check out** — Review the pricing, add to cart, and complete payment securely through Stripe. You'll get a confirmation email right away!\n\nAfter enrolling, you can view your schedule anytime under the Schedule tab on your dashboard. 📅\n\nNot sure which option applies to you? Email us at info@marsacademy.ai and we'll point you in the right direction! 🚀",
  },
  {
    tags: ["scholarship", "financial aid", "afford", "assistance", "can't afford", "cost help"],
    answer: "Scholarships may be available depending on your school and situation. Email us at info@marsacademy.ai and our team will walk you through your options.",
  },
  {
    tags: ["sibling", "siblings", "discount", "multiple kids", "more than one child", "brother", "sister"],
    answer: "Yes — families enrolling more than one child receive a 10% sibling discount, so every kid in the family can join in! 🚀 This applies to direct website registration; discounts may vary for schools where registration is handled through the PTA or another third party.",
  },
  {
    tags: ["bring mars academy", "bring to my school", "bring to my organization", "partner", "new school", "start a program", "offer at my school", "church", "community center", "golf club"],
    answer: "We'd love to hear from you! Email us at info@marsacademy.ai with a bit about your organization, and our team will follow up with everything you need to bring Mars Academy to your students.",
  },
  {
    tags: ["weather", "snow day", "school closed", "school closure", "reschedule", "rescheduled", "make up class", "force majeure", "instructor unavailable", "session moved"],
    answer: "If a session must be cancelled or rescheduled due to school closures, weather, instructor unavailability, or other circumstances beyond our control, we'll notify affected families and work to reschedule the session or provide credit where possible.",
  },
  {
    tags: ["delete my data", "delete data", "access my data", "my information", "child's information", "child's personal information", "personal information", "data request", "remove my data", "data rights"],
    answer: "You can request to correct or delete your or your child's personal information at any time by emailing us at info@marsacademy.ai.",
  },
  {
    tags: ["responsible for my child", "liable for my child", "property damage", "indemnify", "indemnification", "child breaks", "child causes damage", "damage during class"],
    answer: "Yes — as outlined in our Terms & Conditions, parents and guardians agree to be responsible for their child's conduct and any property damage caused during sessions.",
  },
  {
    tags: ["photo", "photos", "picture", "pictures", "video", "recording", "media consent", "opt out photo", "photograph"],
    answer: "Mars Academy may photograph or record sessions for educational and promotional purposes. If you'd prefer your child not appear in any photos or videos, just notify us in writing at info@marsacademy.ai.",
  },
  {
    tags: ["cookies", "cookie policy", "tracking", "google analytics", "data collection website"],
    answer: "We use cookies and tools like Google Analytics to understand how visitors use our website and to help us improve our services. You can control or disable cookies through your browser settings.",
  },
  {
    tags: ["kids use website", "child use website", "can my child sign in", "can my child create an account", "who can create account"],
    answer: "This website and account creation are intended for use by parents and legal guardians. Children should only use the site under a parent or guardian's supervision.",
  },
  {
    tags: ["contact", "reach", "email", "phone", "talk", "speak", "question", "help"],
    answer: "You can reach us at:\n📧 info@marsacademy.ai\n📞 (202)-596-5089\n\nWe're happy to answer any questions you have!",
  },
  {
    tags: ["become a teacher", "apply to teach", "teach with", "want to teach", "teaching position", "teach for", "join as", "work for", "work with us", "apply"],
    answer: "We'd love to have you! Head to the 'Teach With Us' tab on our website to fill out an application. It only takes a few minutes and our team will follow up with you. 🧑‍🏫",
  },
  {
    tags: ["instructor", "teacher", "who teach", "staff", "qualified", "educator", "background check", "background checked", "safe", "vetted"],
    answer: "Mars Academy classes are taught by high school and college students who are passionate about technology and love working with kids. Every instructor and staff member is FCPS background checked and certified on the Mars Academy curriculum before ever leading a class. 🎓",
  },
  {
    tags: ["schedule", "when", "days", "time", "spring", "summer", "dates"],
    answer: "We run Spring and Summer sessions at each partner school. Classes are held once a week on various days depending on the school. For exact schedules, head to the Programs page and select your school to see available sessions and times!",
  },
  {
    tags: ["badge", "achievement", "progress", "track", "report", "parent dashboard", "dashboard"],
    answer: "We use a badge-based progress system — visual achievements that motivate kids and give parents clear insight into what their child is learning. You can track everything in the Parent Dashboard after enrolling. 🏆",
  },
  {
    tags: ["digit infrastructure", "data center", "cloud", "infrastructure", "internet"],
    answer: "Yes! One of the things that makes Mars Academy unique is that we teach kids how digit infrastructure and the cloud actually work — the servers, networks, and digital systems that power the internet. We make it visual, fun, and age-appropriate. ☁️🏢",
  },
  {
    tags: ["how are you", "how r you", "how are u", "you doing", "you good", "hows it going", "how's it going", "how do you do", "you okay", "are you okay"],
    answer: "Good! What can I help you with? 😊",
  },
  {
    tags: ["hello", "hi", "hey", "good morning", "good afternoon", "howdy", "sup"],
    answer: "Hi there! 👋 I'm Nova, Mars Academy's assistant. I'm here to help answer any questions you have about our programs, enrollment, curriculum, or anything else. What would you like to know?",
  },
  {
    tags: ["thank", "thanks", "appreciate", "helpful", "great", "awesome", "perfect"],
    answer: "You're so welcome! 😊 Feel free to ask if anything else comes to mind. We'd love to have your child join us at Mars Academy!",
  },
  {
    tags: ["bye", "goodbye", "see you", "later", "cya"],
    answer: "Goodbye! 🚀 If you ever have more questions, we're always here. Hope to see your little astronaut at Mars Academy soon!",
  },
];

const FALLBACK = "That's a great question! I don't have a specific answer for that just yet. For anything I can't cover, please reach out to us directly at info@marsacademy.ai or call (202)-596-5089 — our team is always happy to help. 😊";

function getReply(input) {
  const lower = input.toLowerCase();
  for (const entry of KB) {
    if (entry.tags.some(tag => lower.includes(tag))) {
      return entry.answer;
    }
  }
  return FALLBACK;
}

const SUGGESTIONS = [
  "What age group is this for?",
  "What will my child learn?",
  "Is it in-person or online?",
  "Do they need a laptop?",
  "How do I enroll?",
  "How do I contact you?",
];

// ── Component ────────────────────────────────────────────────────────────────
export default function ChatBot() {
  const [open, setOpen]       = useState(false);
  const [messages, setMessages] = useState([
    { from: "bot", text: "Hi! I'm Nova 👋 — Mars Academy's assistant. Ask me anything about our programs, curriculum, pricing, or enrollment!" },
  ]);
  const [input, setInput]     = useState("");
  const [typing, setTyping]   = useState(false);
  const [pulse, setPulse]     = useState(true);
  const bottomRef             = useRef(null);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing, open]);

  // Stop pulsing after first open
  useEffect(() => {
    if (open) setPulse(false);
  }, [open]);

  function send(text) {
    const msg = text || input.trim();
    if (!msg) return;
    setInput("");
    setMessages(prev => [...prev, { from: "user", text: msg }]);
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setMessages(prev => [...prev, { from: "bot", text: getReply(msg) }]);
    }, 900 + Math.random() * 400);
  }

  return (
    <>
      {/* ── Floating button ─────────────────────────────── */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          position: "fixed",
          bottom: 28,
          right: 28,
          zIndex: 10000,
          width: 58,
          height: 58,
          borderRadius: "50%",
          background: open
            ? "linear-gradient(135deg, #9B5CF6, #06B6D4)"
            : "linear-gradient(135deg, #FFD93D, #FF6B6B)",
          boxShadow: pulse
            ? "0 0 0 0 rgba(255,217,61,0.6)"
            : open
              ? "0 8px 28px rgba(155,92,246,0.5)"
              : "0 8px 24px rgba(255,217,61,0.35)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          fontSize: "1.5rem",
          transition: "transform 0.2s, box-shadow 0.3s, background 0.3s",
          animation: pulse ? "chatPulse 2s infinite" : "none",
          userSelect: "none",
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.1)"; }}
        onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}
        title="Chat with Nova"
      >
        {open ? "✕" : "💬"}
      </div>

      {/* ── Chat window ─────────────────────────────────── */}
      {open && (
        <div style={{
          position: "fixed",
          bottom: 98,
          right: 28,
          zIndex: 9999,
          width: "min(380px, calc(100vw - 40px))",
          height: 520,
          display: "flex",
          flexDirection: "column",
          background: "linear-gradient(160deg, #0f1b2d 0%, #0a1220 100%)",
          border: "1.5px solid rgba(255,255,255,0.1)",
          borderRadius: 24,
          boxShadow: "0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(155,92,246,0.12)",
          overflow: "hidden",
          animation: "chatSlideUp 0.25s cubic-bezier(0.22,1,0.36,1)",
        }}>

          {/* Header */}
          <div style={{
            padding: "1rem 1.2rem",
            background: "linear-gradient(135deg, rgba(155,92,246,0.18) 0%, rgba(6,182,212,0.08) 100%)",
            borderBottom: "1px solid rgba(255,255,255,0.07)",
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexShrink: 0,
          }}>
            <div style={{
              width: 38, height: 38, borderRadius: "50%",
              background: "linear-gradient(135deg, #9B5CF6, #06B6D4)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "1.2rem", flexShrink: 0,
              boxShadow: "0 0 12px rgba(155,92,246,0.4)",
            }}>
              {BOT_AVATAR}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: "'Fredoka One', cursive", fontSize: "1rem", color: "#fff", lineHeight: 1.2 }}>{BOT_NAME}</div>
              <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#10B981", display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#10B981", display: "inline-block" }} />
                Online · Mars Academy Assistant
              </div>
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: "auto", padding: "1rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {messages.map((m, i) => (
              <div key={i} style={{ display: "flex", justifyContent: m.from === "user" ? "flex-end" : "flex-start", gap: 8, alignItems: "flex-end" }}>
                {m.from === "bot" && (
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: "linear-gradient(135deg,#9B5CF6,#06B6D4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.9rem", flexShrink: 0 }}>
                    {BOT_AVATAR}
                  </div>
                )}
                <div style={{
                  maxWidth: "78%",
                  padding: "0.65rem 0.95rem",
                  borderRadius: m.from === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                  background: m.from === "user"
                    ? "linear-gradient(135deg, #9B5CF6, #06B6D4)"
                    : "rgba(255,255,255,0.07)",
                  border: m.from === "bot" ? "1px solid rgba(255,255,255,0.09)" : "none",
                  color: "#fff",
                  fontSize: "0.84rem",
                  fontWeight: 600,
                  lineHeight: 1.65,
                  whiteSpace: "pre-line",
                  boxShadow: m.from === "user" ? "0 4px 14px rgba(155,92,246,0.25)" : "none",
                }}>
                  {m.text}
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {typing && (
              <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: "linear-gradient(135deg,#9B5CF6,#06B6D4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.9rem" }}>
                  {BOT_AVATAR}
                </div>
                <div style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.09)", borderRadius: "18px 18px 18px 4px", padding: "0.7rem 1rem", display: "flex", gap: 5, alignItems: "center" }}>
                  {[0, 1, 2].map(d => (
                    <span key={d} style={{ width: 7, height: 7, borderRadius: "50%", background: "rgba(155,92,246,0.7)", display: "inline-block", animation: `typingDot 1.2s ${d * 0.2}s infinite ease-in-out` }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Suggestions */}
          {messages.length === 1 && (
            <div style={{ padding: "0 0.9rem 0.6rem", display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.4rem", flexShrink: 0 }}>
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => send(s)}
                  style={{
                    background: "rgba(155,92,246,0.1)",
                    border: "1px solid rgba(155,92,246,0.25)",
                    borderRadius: 10,
                    color: "rgba(255,255,255,0.75)",
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    padding: "0.3rem 0.4rem",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    transition: "background 0.15s, color 0.15s",
                    textAlign: "center",
                    lineHeight: 1.35,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background="rgba(155,92,246,0.25)"; e.currentTarget.style.color="#fff"; }}
                  onMouseLeave={e => { e.currentTarget.style.background="rgba(155,92,246,0.1)"; e.currentTarget.style.color="rgba(255,255,255,0.75)"; }}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div style={{
            padding: "0.75rem 1rem",
            borderTop: "1px solid rgba(255,255,255,0.07)",
            display: "flex",
            gap: 8,
            alignItems: "center",
            background: "rgba(0,0,0,0.2)",
            flexShrink: 0,
          }}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && send()}
              placeholder="Ask a question..."
              style={{
                flex: 1,
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 50,
                padding: "0.55rem 1rem",
                color: "#fff",
                fontSize: "0.85rem",
                fontWeight: 600,
                fontFamily: "inherit",
                outline: "none",
              }}
              onFocus={e => { e.target.style.borderColor = "rgba(155,92,246,0.5)"; }}
              onBlur={e => { e.target.style.borderColor = "rgba(255,255,255,0.1)"; }}
            />
            <button
              onClick={() => send()}
              disabled={!input.trim()}
              style={{
                width: 38, height: 38,
                borderRadius: "50%",
                border: "none",
                background: input.trim() ? "linear-gradient(135deg,#9B5CF6,#06B6D4)" : "rgba(255,255,255,0.07)",
                color: "#fff",
                fontSize: "1rem",
                cursor: input.trim() ? "pointer" : "default",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
                transition: "background 0.2s, transform 0.15s",
                boxShadow: input.trim() ? "0 4px 14px rgba(155,92,246,0.35)" : "none",
              }}
              onMouseEnter={e => { if (input.trim()) e.currentTarget.style.transform = "scale(1.1)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}
            >
              ➤
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes chatSlideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }
        @keyframes chatPulse {
          0%   { box-shadow: 0 0 0 0 rgba(255,217,61,0.6); }
          70%  { box-shadow: 0 0 0 14px rgba(255,217,61,0); }
          100% { box-shadow: 0 0 0 0 rgba(255,217,61,0); }
        }
        @keyframes typingDot {
          0%, 60%, 100% { transform: translateY(0);    opacity: 0.4; }
          30%            { transform: translateY(-5px); opacity: 1;   }
        }
      `}</style>
    </>
  );
}
